import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Steps, Select, message, Popconfirm, Divider, Tooltip, Spin, Alert, Empty } from "antd";
import { PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { ButtonItem, InputItem, InputNumberItem, ModalItem, SelectItem, SwitchItem } from "../../../../../Components/Items";
import LienzoMapaWms from "../../MapaWms/LienzoMapaWms";
import { TIPOS_SECCION, descripcionTipo, AyudaTiposSeccion } from "./tiposSeccionConstants";

const { Option } = Select;
const { Step } = Steps;

const TIPOS_LAYOUT = [
  { value: "normal", label: "Pasillos normales (racks de un solo lado)" },
  { value: "doble_cara", label: "Un pasillo de doble cara" },
  { value: "doble_cara_multiple", label: "Varios pasillos de doble cara con cruces entre ellos" },
];

const seccionInicial = () => ({
  nombre: "",
  tipo: "almacenamiento",
  tipoLayout: "normal",
  pasillos: 2,
  racksPorPasillo: 12,
  racksPorLado: 12,
  paresDobles: 2,
  conCruce: true,
  racksAmboLados: true,
  espacioRackX: 90,
  espacioPasilloY: 230, // con racksAmboLados el backend exige un mínimo (~228) para que los racks no se encimen entre pasillos
  numeroColumnas: 2,
  numeroNiveles: 1,
  numeroProfundidades: 1,
});

//Asistente para generar un mapa a la medida: el usuario define cuántas secciones quiere y, para cada una,
//su estilo de pasillo (normal / un pasillo doble cara / varios pasillos doble cara con cruces), cuántos
//pasillos/racks tiene y las distancias entre ellos - el backend arma todo el grafo + los racks a partir
//de esto (mismo motor que los mapas de ejemplo, ver generadorMapaEjemplo.utils.js/generarMapaPersonalizado).
//Las ubicaciones ya dadas de alta en el almacén se reparten solas entre los racks generados.
//
const AsistenteMapaPersonalizado = ({ visible, onClose, empresaId, proyectoId, headersToken, onGenerado }) => {
  const [paso, setPaso] = useState(0);
  const [nombreMapa, setNombreMapa] = useState("Mapa personalizado");
  const [secciones, setSecciones] = useState([seccionInicial()]);
  const [conTransicion, setConTransicion] = useState(false);
  const [generando, setGenerando] = useState(false);

  const [previewData, setPreviewData] = useState(null);
  const [cargandoPreview, setCargandoPreview] = useState(false);
  const [errorPreview, setErrorPreview] = useState(null);
  const peticionRef = useRef(0); 
  const reiniciar = () => {
    setPaso(0);
    setNombreMapa("Mapa personalizado");
    setSecciones([seccionInicial()]);
    setConTransicion(false);
    setPreviewData(null);
    setErrorPreview(null);
  };

  const cerrar = () => {
    reiniciar();
    onClose();
  };

  const actualizarSeccion = (indice, cambios) => {
    setSecciones((prev) => prev.map((s, i) => (i === indice ? { ...s, ...cambios } : s)));
  };

  const agregarSeccion = () => setSecciones((prev) => [...prev, seccionInicial()]);
  const quitarSeccion = (indice) => setSecciones((prev) => prev.filter((_, i) => i !== indice));

  const moverSeccion = (indice, direccion) => {
    setSecciones((prev) => {
      const destino = indice + direccion;
      if (destino < 0 || destino >= prev.length) return prev;
      const copia = [...prev];
      [copia[indice], copia[destino]] = [copia[destino], copia[indice]];
      return copia;
    });
  };
  useEffect(() => {
    if (!visible) return undefined;
    const idPeticion = ++peticionRef.current;
    setCargandoPreview(true);
    const temporizador = setTimeout(async () => {
      try {
        const res = await axios.post(
          "mapaAlmacen/previewMapaPersonalizado",
          {
            empresaId,
            proyectoId,
            configuracion: { nombre: nombreMapa, conTransicion: secciones.length > 1 && conTransicion, secciones },
          },
          { headers: headersToken }
        );
        if (idPeticion !== peticionRef.current) return;
        if (res.data.success === 2) {
          setPreviewData({ secciones: res.data.secciones, nodos: res.data.nodos, conexiones: res.data.conexiones, racks: res.data.racks });
          setErrorPreview(null);
        } else {
          setErrorPreview(res.data.error || "No se pudo generar la vista previa");
        }
      } catch (error) {
        if (idPeticion !== peticionRef.current) return;
        console.error("Error al previsualizar el mapa:", error);
        setErrorPreview("No se pudo generar la vista previa");
      } finally {
        if (idPeticion === peticionRef.current) setCargandoPreview(false);
      }
    }, 400);
    return () => clearTimeout(temporizador);
  }, [visible, empresaId, proyectoId, headersToken, nombreMapa, secciones, conTransicion]);

  const siguiente = () => {
    if (paso === 0) {
      if (secciones.some((s) => !s.nombre)) {
        message.warning("Captura un nombre para cada sección");
        return;
      }
    }
    setPaso((p) => p + 1);
  };
  const anterior = () => setPaso((p) => p - 1);

  const generar = async () => {
    setGenerando(true);
    try {
      const res = await axios.post(
        "mapaAlmacen/generarMapaPersonalizado",
        {
          empresaId,
          proyectoId,
          configuracion: { nombre: nombreMapa, conTransicion: secciones.length > 1 && conTransicion, secciones },
        },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        message.success(
          `Mapa generado: ${res.data.totalSecciones} sección(es), ${res.data.totalPasillos} pasillo(s), ${res.data.totalNodos} nodos, ${res.data.totalConexiones} conexiones, ${res.data.totalRacks} racks, ${res.data.totalUbicacionesAsignadas}/${res.data.totalUbicacionesDisponibles} ubicaciones asignadas`
        );
        onGenerado();
        cerrar();
      } else {
        message.error(res.data.error || "No se pudo generar el mapa personalizado");
      }
    } catch (error) {
      console.error("Error al generar el mapa personalizado:", error);
      message.error("No se pudo generar el mapa personalizado");
    } finally {
      setGenerando(false);
    }
  };

  return (
    <ModalItem
      title="Asistente: generar mapa a la medida"
      visible={visible}
      onCancel={cerrar}
      width={1180}
      style={{ maxWidth: "96vw", top: 20 }}
      footer={null}
      spinning={false}
    >
      <div style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
        {/* Columna izquierda: configuración (wizard) */}
        <div style={{ flex: "0 0 560px", maxWidth: 560, maxHeight: "74vh", overflowY: "auto", paddingRight: 8 }}>
          <Steps current={paso} size="small" style={{ marginBottom: 24 }}>
            <Step title="Secciones" />
            <Step title="Pasillos y racks" />
            <Step title="Revisar y generar" />
          </Steps>

          {paso === 0 && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4, color: "#6b7280" }}>Nombre del mapa</div>
                <InputItem value={nombreMapa} onChange={(e) => setNombreMapa(e.target.value)} />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#6b7280", fontSize: 12 }}>
                <span>Tipo de cada sección</span>
                <Tooltip title={AyudaTiposSeccion} placement="right">
                  <InfoCircleOutlined style={{ cursor: "help", color: "#8a93a6" }} />
                </Tooltip>
              </div>

              {secciones.map((seccion, indice) => (
                <div key={indice} style={{ border: "1px solid #eef0f6", borderRadius: 8, padding: 10, marginBottom: 10 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Nombre de la sección {indice + 1}</div>
                      <InputItem value={seccion.nombre} onChange={(e) => actualizarSeccion(indice, { nombre: e.target.value })} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Tipo</div>
                      <SelectItem width="100%" value={seccion.tipo} onChange={(value) => actualizarSeccion(indice, { tipo: value })}>
                        {TIPOS_SECCION.map((t) => (
                          <Option key={t.value} value={t.value} title={t.descripcion}>
                            {t.label}
                          </Option>
                        ))}
                      </SelectItem>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <Tooltip title="Subir (más a la izquierda en el mapa)">
                        <ButtonItem size="small" icon={<ArrowUpOutlined />} disabled={indice === 0} onClick={() => moverSeccion(indice, -1)} />
                      </Tooltip>
                      <Tooltip title="Bajar (más a la derecha en el mapa)">
                        <ButtonItem
                          size="small"
                          icon={<ArrowDownOutlined />}
                          disabled={indice === secciones.length - 1}
                          onClick={() => moverSeccion(indice, 1)}
                        />
                      </Tooltip>
                      {secciones.length > 1 && (
                        <Tooltip title="Quitar sección">
                          <ButtonItem size="small" danger icon={<DeleteOutlined />} onClick={() => quitarSeccion(indice)} />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "#8a93a6", marginTop: 6 }}>{descripcionTipo(seccion.tipo)}</div>
                </div>
              ))}

              <ButtonItem icon={<PlusOutlined />} onClick={agregarSeccion}>
                Agregar sección
              </ButtonItem>
            </div>
          )}

          {paso === 1 && (
            <div>
              {secciones.map((seccion, indice) => (
                <div key={indice} style={{ border: "1px solid #eef0f6", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 12 }}>{seccion.nombre || `Sección ${indice + 1}`}</div>

                  <div style={{ marginBottom: 12 }}>
                    <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Estilo de pasillo</div>
                    <SelectItem width="100%" value={seccion.tipoLayout} onChange={(value) => actualizarSeccion(indice, { tipoLayout: value })}>
                      {TIPOS_LAYOUT.map((t) => (
                        <Option key={t.value} value={t.value}>
                          {t.label}
                        </Option>
                      ))}
                    </SelectItem>
                  </div>

                  {seccion.tipoLayout === "normal" && (
                    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Pasillos</div>
                        <InputNumberItem style={{ width: "100%" }} min={1} value={seccion.pasillos} onChange={(v) => actualizarSeccion(indice, { pasillos: v })} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Racks por pasillo</div>
                        <InputNumberItem
                          style={{ width: "100%" }}
                          min={1}
                          value={seccion.racksPorPasillo}
                          onChange={(v) => actualizarSeccion(indice, { racksPorPasillo: v })}
                        />
                      </div>
                    </div>
                  )}

                  {seccion.tipoLayout === "normal" && (
                    <div style={{ display: "flex", gap: 24, marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <SwitchItem checked={seccion.racksAmboLados} onChange={(v) => actualizarSeccion(indice, { racksAmboLados: v })} />
                        <span style={{ fontSize: 12, color: "#6b7280" }}>Racks a ambos lados del pasillo</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <SwitchItem checked={seccion.conCruce} onChange={(v) => actualizarSeccion(indice, { conCruce: v })} />
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Cruce a la mitad{seccion.racksAmboLados ? " (deja un hueco sin racks ahí)" : ""}
                        </span>
                      </div>
                    </div>
                  )}

                  {seccion.tipoLayout === "doble_cara" && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Racks por lado</div>
                      <InputNumberItem
                        style={{ width: "100%" }}
                        min={1}
                        value={seccion.racksPorLado}
                        onChange={(v) => actualizarSeccion(indice, { racksPorLado: v })}
                      />
                    </div>
                  )}

                  {seccion.tipoLayout === "doble_cara_multiple" && (
                    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Pares de pasillos</div>
                        <InputNumberItem
                          style={{ width: "100%" }}
                          min={2}
                          value={seccion.paresDobles}
                          onChange={(v) => actualizarSeccion(indice, { paresDobles: v })}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Racks por lado</div>
                        <InputNumberItem
                          style={{ width: "100%" }}
                          min={1}
                          value={seccion.racksPorLado}
                          onChange={(v) => actualizarSeccion(indice, { racksPorLado: v })}
                        />
                      </div>
                      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, marginTop: 18 }}>
                        <SwitchItem checked={seccion.conCruce} onChange={(v) => actualizarSeccion(indice, { conCruce: v })} />
                        <span style={{ fontSize: 12, color: "#6b7280" }}>Cruces entre pares</span>
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Distancia entre racks (px)</div>
                      <InputNumberItem
                        style={{ width: "100%" }}
                        min={40}
                        value={seccion.espacioRackX}
                        onChange={(v) => actualizarSeccion(indice, { espacioRackX: v })}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Distancia entre pasillos (px)</div>
                      <InputNumberItem
                        style={{ width: "100%" }}
                        min={80}
                        value={seccion.espacioPasilloY}
                        onChange={(v) => actualizarSeccion(indice, { espacioPasilloY: v })}
                      />
                      {seccion.tipoLayout === "normal" && seccion.racksAmboLados && (
                        <div style={{ fontSize: 11, color: "#8a93a6", marginTop: 2 }}>Con racks a ambos lados se usa un mínimo de 228px, aunque pongas menos.</div>
                      )}
                    </div>
                  </div>

                  <Divider style={{ margin: "8px 0" }} />
                  <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Configuración por defecto de cada rack generado</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4, color: "#8a93a6", fontSize: 11 }}>Columnas</div>
                      <InputNumberItem
                        style={{ width: "100%" }}
                        min={1}
                        value={seccion.numeroColumnas}
                        onChange={(v) => actualizarSeccion(indice, { numeroColumnas: v })}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4, color: "#8a93a6", fontSize: 11 }}>Niveles</div>
                      <InputNumberItem
                        style={{ width: "100%" }}
                        min={1}
                        value={seccion.numeroNiveles}
                        onChange={(v) => actualizarSeccion(indice, { numeroNiveles: v })}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4, color: "#8a93a6", fontSize: 11 }}>Profundidades</div>
                      <InputNumberItem
                        style={{ width: "100%" }}
                        min={1}
                        value={seccion.numeroProfundidades}
                        onChange={(v) => actualizarSeccion(indice, { numeroProfundidades: v })}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {paso === 2 && (
            <div>
              {secciones.length > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <SwitchItem checked={conTransicion} onChange={setConTransicion} />
                  <span style={{ color: "#6b7280" }}>Conectar las secciones entre sí con un nodo de transición</span>
                </div>
              )}
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Resumen</div>
              {secciones.map((seccion, indice) => (
                <div key={indice} style={{ marginBottom: 8, padding: 8, background: "#f7f8fa", borderRadius: 6 }}>
                  <strong>{seccion.nombre || `Sección ${indice + 1}`}</strong> ({TIPOS_SECCION.find((t) => t.value === seccion.tipo)?.label}) —{" "}
                  {TIPOS_LAYOUT.find((t) => t.value === seccion.tipoLayout)?.label}
                  {seccion.tipoLayout === "normal" &&
                    `, ${seccion.pasillos} pasillo(s) x ${seccion.racksPorPasillo} racks${seccion.racksAmboLados ? " (ambos lados)" : ""}`}
                  {seccion.tipoLayout === "doble_cara" && `, ${seccion.racksPorLado} racks por lado`}
                  {seccion.tipoLayout === "doble_cara_multiple" && `, ${seccion.paresDobles} pares x ${seccion.racksPorLado} racks por lado`}
                </div>
              ))}
              <p style={{ color: "#8a93a6", fontSize: 12, marginTop: 12 }}>
                Las ubicaciones ya dadas de alta en este almacén se repartirán automáticamente entre los racks generados (no se
                crean ubicaciones nuevas). Esto reemplaza el mapa activo actual (no se borra, queda desactivado).
              </p>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
            <ButtonItem danger onClick={cerrar}>Cancelar</ButtonItem>
            <div style={{ display: "flex", gap: 8 }}>
              {paso > 0 && <ButtonItem  naranja onClick={anterior}>Atrás</ButtonItem>}
              {paso < 2 && (
                <ButtonItem lima type="primary" onClick={siguiente}>
                  Siguiente
                </ButtonItem>
              )}
              {paso === 2 && (
                <Popconfirm
                  title="Esto reemplaza el mapa activo actual (no se borra, queda desactivado). ¿Continuar?"
                  onConfirm={generar}
                  okText="Generar"
                  cancelText="Cancelar"
                >
                  <ButtonItem type="primary" loading={generando}>
                    Generar mapa
                  </ButtonItem>
                </Popconfirm>
              )}
            </div>
          </div>
        </div>

        {/* Columna derecha: maqueta en vivo (dry-run) */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontWeight: 600, color: "#4b5563" }}>Vista previa del almacén</span>
            {cargandoPreview && <Spin size="small" />}
          </div>
          {errorPreview && <Alert type="warning" showIcon message={errorPreview} style={{ marginBottom: 8 }} />}
          <div style={{ flex: 1, border: "1px solid #eef0f6", borderRadius: 8, overflow: "hidden", background: "#fafbfc", minHeight: 420 }}>
            {previewData && (previewData.nodos || []).length > 0 ? (
              <LienzoMapaWms datosMapa={previewData} rackIdsResaltados={new Set()} onNodeDoubleClick={() => {}} altura="72vh" />
            ) : (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 420 }}>
                <Empty description={cargandoPreview ? "Generando vista previa…" : "Configura una sección para ver la maqueta"} />
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#8a93a6", marginTop: 6 }}>
            Es exactamente lo que se guardará. Después de generar podrás moverlo y ajustarlo a mano en el editor.
          </div>
        </div>
      </div>
    </ModalItem>
  );
};

export default AsistenteMapaPersonalizado;
